import {
  ButtonDS,
  PageDS,
  SelectDS,
  Spacer,
  ToastContainerDS,
  triggerToast,
} from "@qivia/ui";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../i18n/i18n";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectAdminUser } from "../homeSlice";
import { devTeam } from "../../constants";
import {
  companyFollowUpAsync,
  selectCompanyFollowUp,
} from "../companiesData/followUpSlice";
import {
  debugAccountSlice,
  selectUpdateUserCompanyAccessStatus,
  updateUserCompanyAccessAsync,
} from "./debugAccountSlice";

const DEBUG_USER_EMAIL = "technical.support@qivia.fr";

export const DebugAccount = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const adminUser = useAppSelector(selectAdminUser);
  const companyFollowUp = useAppSelector(selectCompanyFollowUp);
  const updateStatus = useAppSelector(selectUpdateUserCompanyAccessStatus);
  const [companySelected, setCompanySelected] = useState<string>("");

  useEffect(() => {
    if (adminUser && !devTeam.includes(adminUser.email)) {
      navigate("/home");
    }
  }, [adminUser, navigate]);

  useEffect(() => {
    void dispatch(companyFollowUpAsync());
  }, [dispatch]);

  const companies = useMemo(
    () => companyFollowUp.filter((company) => company.companyGroup !== null),
    [companyFollowUp],
  );

  const options = useMemo(
    () =>
      companies.map((company) => ({
        label: company.name,
        value: company.companyUuid,
      })),
    [companies],
  );

  const valueOption = useMemo(
    () => options.find((option) => option.value === companySelected),
    [companySelected, options],
  );

  const clickToSubmit = useCallback(() => {
    const company = companies.find((c) => c.companyUuid === companySelected);
    if (!company || company.companyGroup === null || !adminUser?.email) return;
    void dispatch(
      updateUserCompanyAccessAsync({
        userEmail: DEBUG_USER_EMAIL,
        companyUuid: company.companyUuid,
        companyGroup: company.companyGroup,
        updatedBy: adminUser.email,
      }),
    );
  }, [adminUser?.email, companies, companySelected, dispatch]);

  useEffect(() => {
    if (updateStatus === "success") {
      triggerToast(t("debugAccount.update.success") || "", "valid");
      dispatch(debugAccountSlice.actions.resetUpdateUserCompanyAccessStatus());
    } else if (updateStatus === "failed") {
      triggerToast(t("debugAccount.update.failure") || "", "error");
      dispatch(debugAccountSlice.actions.resetUpdateUserCompanyAccessStatus());
    }
  }, [updateStatus, t, dispatch]);

  return (
    <PageDS title={t("debugAccount.title")} toaster={<ToastContainerDS />}>
      <Spacer y={1} />
      <StyledForm>
        <SelectDS
          label={t("debugAccount.companyLabel")}
          value={valueOption}
          options={options}
          onChange={(selectedOption) =>
            setCompanySelected(selectedOption ? selectedOption.value : "")
          }
          allWidth
        />
        <Spacer y={1} />
        <ButtonDS
          text={t("debugAccount.submit")}
          format="fill"
          buttonType="primary"
          onClick={clickToSubmit}
          disabled={!companySelected || updateStatus === "processing"}
        />
      </StyledForm>
    </PageDS>
  );
};

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 30rem;
`;
